import { useRef, useState } from 'react';

import {
	autocompleteClasses,
	AutocompleteInputChangeReason,
	ListItem,
	ListItemText,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import useSWRInfinite from 'swr/infinite';

import { AsyncAutocomplete, AsyncAutocompleteProps, Tooltip } from '~components';
import { OffsetPagedResults } from '~interfaces';
import i18n from '~lib/i18n';

import Device from '../../interfaces/device';
import SkcDevicesService from '../../services/skcDevicesService';

const service = new SkcDevicesService();

interface SkcDeviceGroupAutocompleteProps
	extends Omit<AsyncAutocompleteProps<Device>, 'options' | 'label'> {
	label?: string;
	count?: number;
	organisationId?: string;
}

const SkcDevicesAutocomplete = ({
	label = i18n.t('device'),
	count = 10,
	organisationId,
	...asyncAutocompleteProps
}: SkcDeviceGroupAutocompleteProps) => {
	const { t } = useTranslation();

	const [searchQuery, setSearchQuery] = useState('');
	const [open, setOpen] = useState(false);

	const total = useRef<number>(undefined);
	const page = useRef(1);

	const getKey = (i: number, previousData: OffsetPagedResults<Device[]>) => {
		if (!open || (previousData && !previousData.hasMore)) {
			// reached the end
			return null;
		}

		page.current = i + 1;

		return [
			service.basePath,
			{
				page: page.current,
				pageSize: count,
				organisationId: organisationId,
				searchQuery: searchQuery,
			},
		] as const;
	};

	const { data, isLoading, isValidating, size, setSize } = useSWRInfinite(
		getKey,
		([_, args]) => service.getDevices(args),
		{
			keepPreviousData: true,
			revalidateFirstPage: false,
			onSuccess: (res) => (total.current = res[0].total),
		}
	);

	/**
	 * Just set the size of the pages
	 */
	const handleOverflow = () => {
		if (total.current != null && (size - 1) * count <= total.current) {
			setSize(size + 1);
		}
	};

	const handleInputChange = (
		e: React.SyntheticEvent<Element, Event>,
		value: string,
		reason: AutocompleteInputChangeReason
	) => {
		setSearchQuery(value);

		asyncAutocompleteProps.onInputChange?.(e, value, reason);
	};

	return (
		<AsyncAutocomplete
			{...asyncAutocompleteProps}
			label={label}
			loading={isLoading || isValidating}
			noOptionsText={t('noResults')}
			onOpen={() => setOpen(true)}
			options={data?.flatMap((el) => el.results) ?? []}
			onInputChange={handleInputChange}
			onOverflow={handleOverflow}
			renderOption={(props, option, state, ownerState) => {
				if (asyncAutocompleteProps.renderOption != null) {
					return asyncAutocompleteProps.renderOption(props, option, state, ownerState);
				}

				const { key, ...propsNoKey } = props;
				return (
					<ListItem
						{...propsNoKey}
						component='li'
						sx={{
							[`&.${autocompleteClasses.option}`]: {
								py: '2px',
							},
						}}
					>
						<ListItemText
							primary={option.label}
							secondary={`${option.id} - ${option.hardwareId}`}
						/>
					</ListItem>
				);
			}}
		/>
	);
};

export default SkcDevicesAutocomplete;
